<template>
 <MarketingShell>
 <div class="tm-integrated tm-privacypolicyscreen">
  <div class="privacy-page" :class="{ 'privacy-dark': isDark }">
    <!-- Nav -->
    

    <div class="privacy-main">
      <!-- Header -->
      <div class="privacy-hero">
        <h1>Privacy Policy</h1>
        <p class="hero-subtitle">How we protect your data</p>
      </div>

      <!-- Content -->
      <div class="privacy-content">
        <!-- Introduction -->
        <div class="intro-card">
          <h2>Your Privacy Matters</h2>
          <p>
            At TradingCandle, we are committed to protecting your privacy and ensuring 
            the security of your personal information. This policy explains how we 
            collect, use, and protect your data.
          </p>
        </div>

        <!-- Privacy Badge -->
        <div class="privacy-badge">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Privacy First Approach
          </h3>
          <p>
            We follow privacy-by-design principles and GDPR compliance to ensure 
            your data is handled with the utmost care and transparency.
          </p>
        </div>

        <!-- Data Collection -->
        <section class="privacy-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            </svg>
            Data Collection
          </h3>
          <p>
            We collect <strong>minimal data</strong> necessary to provide 
            and improve our services:
          </p>
          
          <div class="data-grid">
            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>Authentication Data</h4>
                <p>Email address, profile information, and authentication tokens managed through Clerk for secure login</p>
              </div>
            </div>

            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 1v6m0 6v6m4.22-13.22l4.24 4.24M1.54 9.96l4.24 4.24M1 12h6m6 0h6"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>App Preferences</h4>
                <p>Theme settings, language preferences, and customization choices</p>
              </div>
            </div>

            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>API Credentials</h4>
                <p>Encrypted storage of your broker API keys for data access</p>
              </div>
            </div>

            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 20V10m-6 10V4M6 20v-6"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>Usage Analytics</h4>
                <p>Anonymous usage data to improve app performance and features</p>
              </div>
            </div>
          </div>
        </section>

        <!-- AI Data Usage -->
        <section class="privacy-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
              <path d="M9 14h6"/>
            </svg>
            AI Data Usage
          </h3>
          <p>
            We use <strong>external AI providers</strong> to enhance your experience 
            with intelligent features and analysis:
          </p>
          
          <div class="data-grid">
            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  <path d="M8 10h.01M12 10h.01M16 10h.01"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>Chat Interactions</h4>
                <p>Your chat messages and AI responses for feature assistance and analysis</p>
              </div>
            </div>

            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 3v18h18"/>
                  <path d="M18 17V9"/>
                  <path d="M13 17V5"/>
                  <path d="M8 17v-3"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>Indicator Generation</h4>
                <p>Prompts and generated content for technical indicator creation and customization</p>
              </div>
            </div>

            <div class="data-item">
              <div class="data-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <div class="data-content">
                <h4>Market Analysis</h4>
                <p>Market data queries and AI-generated insights for trading analysis</p>
              </div>
            </div>
          </div>

          <div class="ai-notes">
            <div class="note-item">
              <span class="note-icon">!</span>
              <div class="note-content">
                <strong>Data Processing:</strong> Your data is processed by external AI services 
                to generate responses and insights. We ensure these providers comply with strict data protection standards.
              </div>
            </div>
            <div class="note-item">
              <span class="note-icon">!</span>
              <div class="note-content">
                <strong>No Training Data:</strong> Your personal data and conversations are not used 
                to train AI models unless you explicitly opt-in.
              </div>
            </div>
            <div class="note-item">
              <span class="note-icon">!</span>
              <div class="note-content">
                <strong>Data Retention:</strong> AI interaction data is retained for 30 days to 
                improve service quality and for troubleshooting purposes.
              </div>
            </div>
          </div>
        </section>

        <!-- Data Protection -->
        <section class="privacy-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Data Protection
          </h3>
          <div class="privacy-list">
            <div class="list-item">
              <span class="list-number">1.</span>
              <div class="list-content">
                <strong>No Personal Information Sharing:</strong> We never sell, 
                rent, or share your personal information with third parties for marketing purposes.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">2.</span>
              <div class="list-content">
                <strong>Secure Data Storage:</strong> All data is encrypted 
                using industry-standard encryption protocols both in transit and at rest.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">3.</span>
              <div class="list-content">
                <strong>GDPR Compliant:</strong> We comply with GDPR and other 
                privacy regulations to protect your rights.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">4.</span>
              <div class="list-content">
                <strong>Regular Security Audits:</strong> We conduct regular 
                security assessments to ensure data protection.
              </div>
            </div>
          </div>
        </section>

        <!-- What We Don't Collect -->
        <section class="privacy-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            What We Don't Collect
          </h3>
          <div class="privacy-list">
            <div class="list-item">
              <span class="list-number">×</span>
              <div class="list-content">
                <strong>Financial Information:</strong> We don't collect or store 
                your bank account details, credit card information, or trading account balances.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">×</span>
              <div class="list-content">
                <strong>Trading History:</strong> We don't access or store your 
                personal trading history or transaction records.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">×</span>
              <div class="list-content">
                <strong>Location Data:</strong> We don't track your physical 
                location unless you explicitly enable location-based features.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">×</span>
              <div class="list-content">
                <strong>Contact Lists:</strong> We don't access your contacts 
                or social media connections.
              </div>
            </div>
          </div>
        </section>

        <!-- Your Rights -->
        <section class="privacy-section">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            Your Rights
          </h3>
          <div class="privacy-list">
            <div class="list-item">
              <span class="list-number">1.</span>
              <div class="list-content">
                <strong>Access:</strong> Request access to your personal data 
                that we hold.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">2.</span>
              <div class="list-content">
                <strong>Correction:</strong> Request correction of inaccurate 
                or incomplete data.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">3.</span>
              <div class="list-content">
                <strong>Deletion:</strong> Request deletion of your personal 
                data where legally permitted.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">4.</span>
              <div class="list-content">
                <strong>Portability:</strong> Request transfer of your data 
                to another service provider.
              </div>
            </div>
            <div class="list-item">
              <span class="list-number">5.</span>
              <div class="list-content">
                <strong>Objection:</strong> Object to processing of your data 
                for legitimate interests.
              </div>
            </div>
          </div>
        </section>

        <!-- Website Link -->
        <div class="website-card">
          <h3>Full Privacy Policy</h3>
          <p>For the complete and detailed Privacy Policy, please visit our website.</p>
          <a href="https://tradingcandle.app/privacy" target="_blank" rel="noopener" class="btn-website">
            Visit Website
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15,3 21,3 21,9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Footer -->
    
  </div>

 </div>
 </MarketingShell>
</template>

<script>
import MarketingShell from "./marketing/MarketingShell.vue";
export default {
  components: { MarketingShell },
  name: 'PrivacyPolicyScreen',
  computed: {
    isDark() {
      return this.$store.getters.isDarkTheme;
    }
  },
  methods: {
    toggleTheme() {
      this.$store.dispatch('toggleTheme');
    }
  }
}
</script>

<style scoped>
.privacy-page {
  min-height: 100vh;
  background: #fafaf8;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0f1923;
  display: flex;
  flex-direction: column;
}

/* Nav */
.privacy-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  border-bottom: 1px solid #e8e5de;
  background: rgba(250, 250, 248, 0.95);
  backdrop-filter: blur(8px);
}

.privacy-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0f1923;
}

.privacy-logo-img {
  height: 32px;
}

.privacy-logo-text {
  font-family: 'DM Serif Display', serif;
  font-size: 20px;
  font-weight: 600;
}

.privacy-nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme-toggle-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d0ccc4;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.theme-toggle-btn:hover {
  background: #f0ede6;
}

.btn-outline {
  padding: 8px 20px;
  border: 1.5px solid #d0ccc4;
  background: transparent;
  color: #2b3a47;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
}

.btn-outline:hover {
  border-color: #0f1923;
  color: #0f1923;
}

/* Main Layout */
.privacy-main {
  flex: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 48px 24px;
  width: 100%;
}

/* Hero Section */
.privacy-hero {
  text-align: center;
  margin-bottom: 48px;
}

.privacy-hero h1 {
  font-family: 'DM Serif Display', serif;
  font-size: 48px;
  letter-spacing: -1px;
  margin: 0 0 16px;
  font-weight: 600;
  color: #0f1923;
}

.hero-subtitle {
  font-size: 18px;
  color: #4a5568;
  line-height: 1.6;
  max-width: 560px;
  margin: 0 auto;
}

/* Content */
.privacy-content {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.intro-card {
  background: linear-gradient(135deg, #d4edda20 0%, #d4edda05 100%);
  border: 1px solid #c3e6cb;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;
  text-align: center;
}

.intro-card h2 {
  font-size: 24px;
  font-weight: 700;
  color: #0f1923;
  margin: 0 0 16px;
}

.intro-card p {
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
  margin: 0;
}

/* Privacy Badge */
.privacy-badge {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
}

.privacy-badge h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #155724;
  margin: 0 0 12px;
}

.privacy-badge p {
  font-size: 14px;
  line-height: 1.5;
  color: #155724;
  margin: 0;
}

/* Sections */
.privacy-section {
  margin-bottom: 48px;
}

.privacy-section h3 {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  color: #0f1923;
  margin: 0 0 20px;
}

.privacy-section h3 svg {
  color: #2962ff;
  flex-shrink: 0;
}

.privacy-section p {
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
  margin: 0 0 24px;
}

.privacy-section strong {
  color: #0f1923;
  font-weight: 600;
}

/* Data Grid */
.data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.data-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.data-icon {
  width: 40px;
  height: 40px;
  background: #2962ff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.data-content {
  flex: 1;
}

.data-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0f1923;
  margin: 0 0 8px;
}

.data-content p {
  font-size: 14px;
  line-height: 1.5;
  color: #4a5568;
  margin: 0;
}

/* AI Notes */
.ai-notes {
  margin-top: 24px;
}

.note-item {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.note-icon {
  font-size: 16px;
  font-weight: 700;
  color: #2962ff;
  min-width: 20px;
}

.note-content {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: #4a5568;
}

.note-content strong {
  color: #0f1923;
  font-weight: 600;
}

/* Privacy List */
.privacy-list {
  margin-top: 24px;
}

.list-item {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.list-number {
  font-size: 16px;
  font-weight: 700;
  color: #2962ff;
  min-width: 20px;
}

.list-number:last-child {
  color: #dc2626;
}

.list-content {
  flex: 1;
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
}

.list-content strong {
  color: #0f1923;
  font-weight: 600;
}

/* Website Card */
.website-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 32px;
  margin-top: 48px;
  text-align: center;
}

.website-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: #166534;
  margin: 0 0 12px;
}

.website-card p {
  font-size: 16px;
  color: #15803d;
  margin: 0 0 24px;
}

.btn-website {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.15s;
}

.btn-website:hover {
  background: #15803d;
  transform: translateY(-1px);
}

/* Footer */
.privacy-footer {
  text-align: center;
  padding: 24px;
  font-size: 13px;
  color: #94a3b8;
  border-top: 1px solid #e8e5de;
}

.privacy-footer p {
  margin: 0 0 4px;
}

.privacy-footer p:last-child {
  margin-bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .privacy-nav {
    padding: 0 16px;
  }
  
  .privacy-main {
    padding: 32px 16px;
  }
  
  .privacy-hero h1 {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .privacy-content {
    padding: 24px;
  }
  
  .privacy-section h3 {
    font-size: 18px;
  }
  
  .data-grid {
    grid-template-columns: 1fr;
  }
}

/* Dark Theme */
.privacy-dark {
  background: #0c1018;
  color: #d1d4dc;
}

.privacy-dark .privacy-nav {
  background: rgba(12, 16, 24, 0.95);
  border-bottom-color: rgba(55, 59, 75, 0.5);
}

.privacy-dark .privacy-logo { color: #e5e7eb; }

.privacy-dark .theme-toggle-btn {
  border-color: rgba(55, 59, 75, 0.6);
  color: #d1d4dc;
}

.privacy-dark .theme-toggle-btn:hover {
  background: rgba(55, 59, 75, 0.4);
}

.privacy-dark .btn-outline {
  border-color: rgba(55, 59, 75, 0.6);
  color: #d1d4dc;
}

.privacy-dark .btn-outline:hover {
  border-color: #e5e7eb;
  color: #fff;
}

.privacy-dark .privacy-hero h1 { color: #e5e7eb; }

.privacy-dark .hero-subtitle { color: #9ca3af; }

.privacy-dark .privacy-content {
  background: #121826;
  border-color: rgba(55, 59, 75, 0.5);
}

.privacy-dark .intro-card {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
  border-color: rgba(34, 197, 94, 0.3);
}

.privacy-dark .intro-card h2 { color: #e5e7eb; }

.privacy-dark .intro-card p { color: #9ca3af; }

.privacy-dark .privacy-badge {
  background: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.3);
}

.privacy-dark .privacy-badge h3 { color: #86efac; }

.privacy-dark .privacy-badge p { color: #4ade80; }

.privacy-dark .privacy-section h3 { color: #e5e7eb; }

.privacy-dark .privacy-section p { color: #cbd5e1; }

.privacy-dark .privacy-section strong { color: #e5e7eb; }

.privacy-dark .data-item {
  background: #1e293b;
  border-color: rgba(55, 59, 75, 0.5);
}

.privacy-dark .data-content h4 { color: #e5e7eb; }

.privacy-dark .data-content p { color: #9ca3af; }

.privacy-dark .note-content { color: #cbd5e1; }

.privacy-dark .note-content strong { color: #e5e7eb; }

.privacy-dark .list-content { color: #cbd5e1; }

.privacy-dark .list-content strong { color: #e5e7eb; }

.privacy-dark .website-card {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.privacy-dark .website-card h3 { color: #86efac; }

.privacy-dark .website-card p { color: #4ade80; }

.privacy-dark .privacy-footer {
  border-top-color: rgba(55, 59, 75, 0.4);
  color: #64748b;
}
</style>
